import { LucideIconsModule } from '../../../shared/lucide-icons/lucide-icons.module';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  NgZone,
  OnDestroy,
  ViewChild,
  inject,
  Input,
  PLATFORM_ID,
} from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { OrDialogService, OrSheetComponent } from '../../../ui';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { Subscription } from 'rxjs';
import {
  ALL_SIDE_MENU_SECTIONS,
  SideMenuItem,
  SideMenuSection,
  buildMobileMoreMenuSections,
  cloneSideMenuSections,
  filterSideMenuSections,
  flattenSideMenuSections,
  toMobileMoreMenuItem,
} from '../../../utils/side-menu.config';
import { allowedPathsForRole } from '../../../utils/rbac-routes.util';
import {
  filterOutletsByUserScope,
  pickScopedOutlet,
} from '../../../utils/outlet-scope.util';
import { SETTINGS_PAGE_PATHS } from '../../../pages/settings/settings-routes.constants';
import { NzCollapseModule } from 'ng-zorro-antd/collapse';
import { ApiService } from '../../../services/api.service';
import { CommonService } from '../../../services/common.service';
import { MediaCacheService } from '../../../services/media-cache.service';
import { ApiCacheService } from '../../../services/api-cache.service';
import { BillingApiService } from '../../../services/billing-api.service';
import { UserAccessService } from '../../../services/user-access.service';
import { combineLatest, startWith } from 'rxjs';
@Component({
  selector: 'app-mobile-header',
  imports: [
    LucideIconsModule,
    NzIconModule,
    NzGridModule,
    NzInputModule,
    NzDropDownModule,
    NzModalModule,
    FormsModule,
    NzToolTipModule,
    NzSpinModule,
    RouterLink,
    NzCollapseModule,
    OrSheetComponent,
  ],
  templateUrl: './mobile-header.component.html',
  styleUrl: './mobile-header.component.css',
})
export class MobileHeaderComponent {
  @ViewChild('searchInput') searchInput!: ElementRef;
  @ViewChild('searchResultsContainer') searchResultsContainer?: ElementRef<HTMLElement>;
  @Input() currentRoute: string = '';

  // Search modal
  isVisible = false;
  isMac: boolean = false;

  // Account bottom sheet (replaces profile dropdown on mobile)
  accountSheetOpen = false;

  // User info
  userName: any = {};
  userRole: any = {};

  searchTimeOut: any;
  searchText: string = '';
  searchType: 'pages' | 'members' = 'pages';
  filteredMembers: any[] = [];
  membersLoading = false;
  activeSearchIndex = -1;
  isKeyboardSearchNav = false;
  private membersSearchTimeout: any;

  selectOutlet: any = {};
  allOutlet: any[] = [];
  private rawOutlets: any[] = [];
  private userOutletScope: string[] = [];
  readonly outletLimit = 50;
  outletPageIndex = 1;
  outletTotalCount = 0;
  outletsLoading = false;
  outletLoadingMore = false;

  more: string[] = [
    '/analytics',
    '/campaigns',
    '/referral',
    '/coupon',
    '/rules',
    '/segments',
    '/products',
    '/transactions',
    '/feedback',
    '/integration',
    '/billing',
    '/redemption',
    '/customer-analytics',
  ];

  private readonly allSideMenuSections = ALL_SIDE_MENU_SECTIONS;

  sideMenuSections: SideMenuSection[] = cloneSideMenuSections(this.allSideMenuSections);

  get sideMenu(): SideMenuItem[] {
    return flattenSideMenuSections(this.sideMenuSections);
  }

  get filteredSearchSections(): SideMenuSection[] {
    const query = this.searchText.trim().toLowerCase();
    if (!query) {
      return [];
    }

    return this.sideMenuSections
      .map((section) => ({
        ...section,
        items: section.items.filter((item) =>
          item.name.toLowerCase().includes(query),
        ),
      }))
      .filter((section) => section.items.length > 0);
  }

  get filteredSideMenuForSearch(): SideMenuSection[] {
    const query = this.searchText.trim().toLowerCase();
    if (!query) {
      return this.sideMenuSections;
    }
    return this.sideMenuSections
      .map((section) => ({
        ...section,
        items: section.items.filter((item) =>
          item.name.toLowerCase().includes(query),
        ),
      }))
      .filter((section) => section.items.length > 0);
  }

  pageTittleHead: string = '';
  dataShareSubscribe!: Subscription;
  private outletSub?: Subscription;
  private billingSub?: Subscription;

  // Plan / trial status (shown as a slim banner on mobile)
  planLabel = '';
  planDaysLeft: number | null = null;
  planProgress = 0;
  planCta = 'Upgrade';
  planLoaded = false;
  planIsTrial = false;

  isPageHeaderFixed = false;
  commonServiceHeader!: Subscription;

  isHeaderScrolled = false;
  isHeaderActive = false;

  private scrollListener?: (event: Event) => void;
  private scrollActiveTimeout: any;

  private route = inject(Router);
  private readonly dialog = inject(OrDialogService);
  private readonly platformId = inject(PLATFORM_ID);
  private api = inject(ApiService);
  private commonServices = inject(CommonService);
  private cdr = inject(ChangeDetectorRef);
  private zone = inject(NgZone);
  private document = inject(DOCUMENT);
  private mediaCache = inject(MediaCacheService);
  private apiCache = inject(ApiCacheService);
  private billingApi = inject(BillingApiService);
  private userAccess = inject(UserAccessService);
  private allowedMenuPaths: readonly string[] | null = null;
  private homePath = '/dashboard';

  private readonly isBrowser = isPlatformBrowser(this.platformId);

  ngOnInit(): void {
    const storedOutlet = localStorage.getItem('selectedOutlet');
    if (storedOutlet) {
      const data = JSON.parse(storedOutlet);
      this.selectOutlet = data.outlet || {};
    }
    this.getAllOutlets();
    this.getLoginData();
    this.loadPlanSummary();

    this.sideMenuSections = cloneSideMenuSections(this.allSideMenuSections);
    this.outletSub = combineLatest([
      this.commonServices.selectedOutlet.pipe(startWith(this.selectOutlet || null)),
      this.userAccess.fetchAccess(),
    ]).subscribe(([outlet, access]) => {
      this.allowedMenuPaths = allowedPathsForRole(access.role);
      this.homePath = access.role === 'biller' ? '/transactions' : '/dashboard';
      const scope = access.outletScope || [];
      if (scope.join(',') !== this.userOutletScope.join(',')) {
        this.userOutletScope = scope;
        this.applyOutletScopeFilter(true);
      }
      this.sideMenuSections = filterSideMenuSections(
        this.allSideMenuSections,
        (outlet as any)?.posName,
        this.allowedMenuPaths,
        access.role,
      );
      this.cdr.detectChanges();
    });

    this.isMac = this.checkPlatform();

    this.dataShareSubscribe = this.commonServices.pageTittle.subscribe(
      (title: any) => {
        const newTitle = title?.trim();
        this.pageTittleHead = newTitle ? newTitle : 'Ownrewards';
        this.cdr.detectChanges();
      },
    );

    this.commonServiceHeader = this.commonServices.isHeaderFixed.subscribe(
      (value: boolean) => {
        setTimeout(() => {
          this.isPageHeaderFixed = value;
          this.cdr.detectChanges();
        });
      },
    );
    this.initializeScrollDetection();
  }

  showBackArrow(): boolean {
    const route = this.currentRoute?.split('?')[0].split(';')[0] || '';
    if (route.startsWith('/mobile-settings')) {
      return true;
    }
    return this.more.some(
      (path) => route === path || route.startsWith(`${path}/`),
    );
  }

  backMenu() {
    const route = this.currentRoute?.split('?')[0].split(';')[0] || '';
    if (route.startsWith('/customer-analytics')) {
      this.pageTittleHead = 'Members';
      this.commonServices.pageTittle.next('Members');
      this.route.navigate(['/members']);
      return;
    }
    this.pageTittleHead = 'Ownrewards';
    this.commonServices.pageTittle.next('Ownrewards');
    this.route.navigate([this.homePath]);
  }

  getActiveMenuTitle(): string {
    const route = this.currentRoute?.split('?')[0].split(';')[0] || '';
    const fromRoute = this.titleForRoute(route);
    // Route wins over a stale pageTittle left by the previous screen.
    if (fromRoute) return fromRoute;
    if (this.pageTittleHead && this.pageTittleHead !== 'Ownrewards' && this.pageTittleHead.trim() !== '') {
      return this.pageTittleHead;
    }
    return 'OwnRewards';
  }

  private titleForRoute(route: string): string {
    if (!route) return '';
    if (route.startsWith('/get-started')) return 'Get started';
    if (route.startsWith('/dashboard')) return 'Home';
    if (route.startsWith('/analytics/retention')) return 'Retention';
    if (route.startsWith('/analytics')) return 'Analytics';
    if (route.startsWith('/campaigns')) return 'Campaigns';
    if (route.startsWith('/referral')) return 'Referrals';
    if (route.startsWith('/members')) return 'Members';
    if (route.startsWith('/customer-analytics')) return 'Member profile';
    if (route.startsWith('/segments')) return 'Segments';
    if (route.startsWith('/loyalty-programs') || route.startsWith('/loyalty/create')) return 'Loyalty';
    if (route.startsWith('/rules')) return 'Automations';
    if (route.startsWith('/journeys')) return 'Timed sequences';
    if (route.startsWith('/qr-codes')) return 'QR Codes';
    if (route.startsWith('/payouts')) return 'Payouts';
    if (route.startsWith('/member-portal/studio')) return 'Portal Studio';
    if (route.startsWith('/member-portal')) return 'Member Portal';
    if (route.startsWith('/rewards')) return 'Rewards';
    if (route.startsWith('/coupon')) return 'Coupons';
    if (route.startsWith('/billing')) return 'Plans & Billing';
    if (route.startsWith('/products')) return 'Products';
    if (route.startsWith('/transactions')) return 'Transactions';
    if (route.startsWith('/feedback')) return 'Feedback';
    if (route.startsWith('/integration')) return 'Integrations';
    if (route.startsWith('/channels')) return 'Channels';
    if (route.startsWith('/api-webhooks')) return 'API & Webhooks';
    if (route.startsWith('/events')) return 'Events';
    if (route.startsWith('/policies')) return 'Policies';
    if (route.startsWith('/redemption')) return 'Redemption';
    if (route.startsWith('/how-it-works')) return 'How it works';
    if (route.startsWith('/settings')) return 'Settings';
    if (route.startsWith('/mobile-setting') || route.startsWith('/mobile-settings')) return 'Settings';
    return '';
  }

  openFilter() {
    this.commonServices.updateActionData('filter');
  }

  openSort() {
    this.commonServices.updateActionData('sort');
  }

  openSearch() {
    this.commonServices.updateActionData('search');
  }

  private initializeScrollDetection(): void {
    if (!this.isBrowser) {
      return;
    }

    this.zone.runOutsideAngular(() => {
      this.scrollListener = (event: Event) => {
        const scrollTop = this.resolveScrollTop(event);
        const shouldCondense = scrollTop > 16;

        this.zone.run(() => this.applyScrollStates(shouldCondense));
      };

      this.document.addEventListener('scroll', this.scrollListener, true);
    });
  }

  private resolveScrollTop(event: Event): number {
    const target = event.target as HTMLElement | Document | null;

    if (target && target instanceof HTMLElement) {
      return target.scrollTop || 0;
    }

    if (target && 'documentElement' in target) {
      const doc = target as Document;
      return doc.documentElement?.scrollTop || doc.body?.scrollTop || 0;
    }

    if (typeof window !== 'undefined') {
      return window.scrollY || 0;
    }

    return 0;
  }

  private applyScrollStates(shouldCondense: boolean): void {
    const scrolledChanged = this.isHeaderScrolled !== shouldCondense;
    this.isHeaderScrolled = shouldCondense;
    this.isHeaderActive = true;

    if (this.scrollActiveTimeout) {
      clearTimeout(this.scrollActiveTimeout);
    }

    this.scrollActiveTimeout = setTimeout(() => {
      this.isHeaderActive = false;
      this.cdr.markForCheck();
    }, 220);

    if (scrolledChanged) {
      this.cdr.markForCheck();
    }
  }

  get navigableSearchResults(): Array<SideMenuItem | any> {
    if (this.searchType === 'pages') {
      return this.filteredSideMenuForSearch.flatMap((section) => section.items);
    }
    const query = this.searchText.trim();
    if (!query || this.membersLoading) {
      return [];
    }
    return this.filteredMembers;
  }

  getPageItemIndex(item: SideMenuItem): number {
    return this.navigableSearchResults.findIndex(
      (entry) => (entry as SideMenuItem).path === item.path,
    );
  }

  isSearchResultActive(index: number): boolean {
    return this.activeSearchIndex === index;
  }

  // Search Modal Methods
  showModal(): void {
    this.isVisible = true;
    this.searchText = '';
    this.searchType = 'pages';
    this.filteredMembers = [];
    this.activeSearchIndex = -1;
    this.isKeyboardSearchNav = false;
    setTimeout(() => {
      this.searchInput?.nativeElement?.focus();
    }, 500);
  }

  handleCancel(): void {
    this.isVisible = false;
  }

  modalclose(): void {
    this.isVisible = false;
  }

  hasNoResults(): boolean {
    const query = this.searchText.trim();
    if (query.length === 0) return false;
    if (this.searchType === 'pages') {
      return this.filteredSideMenuForSearch.length === 0;
    } else {
      return this.filteredMembers.length === 0 && !this.membersLoading;
    }
  }

  changeSearchType(type: 'pages' | 'members'): void {
    this.searchType = type;
    this.searchText = '';
    this.filteredMembers = [];
    this.activeSearchIndex = -1;
    this.isKeyboardSearchNav = false;
    if (this.membersSearchTimeout) clearTimeout(this.membersSearchTimeout);
    setTimeout(() => this.searchInput?.nativeElement?.focus(), 0);
  }

  onSearchTextChange(): void {
    this.activeSearchIndex = -1;
    this.isKeyboardSearchNav = false;
    if (this.searchType !== 'members') return;
    if (this.membersSearchTimeout) clearTimeout(this.membersSearchTimeout);
    const query = this.searchText.trim();
    if (!query) {
      this.filteredMembers = [];
      return;
    }
    this.membersSearchTimeout = setTimeout(() => this.searchMembers(query), 300);
  }

  searchMembers(query: string): void {
    const tokenData: string = localStorage.getItem('TtZUw0Q87E647ac8j0gQHiCmBhczph') || '';
    if (!tokenData) return;
    this.membersLoading = true;
    const outletId = (typeof this.selectOutlet === 'object') ? this.selectOutlet?._id : this.selectOutlet;
    const payload = { outletId: outletId || undefined, filterValue: query };
    const header = { headers: { Authorization: 'Bearer ' + tokenData } };
    this.api.commonPostMethod('loyalty-customer/get-All-customerPoints?page=1&limit=50', payload, header)
      .subscribe({
        next: (response: any) => {
          if (this.searchText.trim() === query) {
            this.filteredMembers = response?.data?.loyaltyCustomer || [];
          }
          this.membersLoading = false;
          this.cdr.detectChanges();
        },
        error: () => {
          this.membersLoading = false;
          this.filteredMembers = [];
          this.cdr.detectChanges();
        }
      });
  }

  goToMemberDetail(member: any): void {
    this.modalclose();
    this.route.navigate(['/customer-analytics'], {
      queryParams: {
        customerId: member._id,
        customerName: member.name || 'Customer',
        phoneNo: member.phoneNo || '',
        fromTab: 'members',
      },
    });
  }

  onSearchInputKeydown(event: KeyboardEvent): void {
    if (!this.isVisible) {
      return;
    }

    const items = this.navigableSearchResults;
    if (event.key === 'ArrowDown') {
      if (items.length === 0) {
        return;
      }
      event.preventDefault();
      this.isKeyboardSearchNav = true;
      this.activeSearchIndex =
        this.activeSearchIndex < items.length - 1
          ? this.activeSearchIndex + 1
          : 0;
      this.scrollActiveSearchResultIntoView();
      this.cdr.markForCheck();
      return;
    }

    if (event.key === 'ArrowUp') {
      if (items.length === 0) {
        return;
      }
      event.preventDefault();
      this.isKeyboardSearchNav = true;
      this.activeSearchIndex =
        this.activeSearchIndex > 0
          ? this.activeSearchIndex - 1
          : items.length - 1;
      this.scrollActiveSearchResultIntoView();
      this.cdr.markForCheck();
      return;
    }

    if (event.key === 'Enter' && this.activeSearchIndex >= 0) {
      const selected = items[this.activeSearchIndex];
      if (!selected) {
        return;
      }
      event.preventDefault();
      this.activateSearchResult(selected);
    }
  }

  onSearchResultsPointerInteraction(): void {
    if (!this.isKeyboardSearchNav) {
      return;
    }
    this.isKeyboardSearchNav = false;
    this.activeSearchIndex = -1;
    this.cdr.markForCheck();
  }

  private activateSearchResult(item: SideMenuItem | any): void {
    if (this.searchType === 'pages') {
      this.modalclose();
      this.route.navigate([(item as SideMenuItem).path]);
      return;
    }
    this.goToMemberDetail(item);
  }

  private scrollActiveSearchResultIntoView(): void {
    setTimeout(() => {
      const container = this.searchResultsContainer?.nativeElement;
      if (!container) {
        return;
      }
      const activeItem = container.querySelector(
        '.search-result-item.active, .mobile-search-result-item.active',
      ) as HTMLElement | null;
      activeItem?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }, 0);
  }

  private checkPlatform(): boolean {
    if (typeof navigator !== 'undefined') {
      return /Mac|iPod|iPhone|iPad/.test(navigator.platform);
    }
    return false;
  }

  @HostListener('window:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    if (
      (this.isMac && event.metaKey && event.key.toLowerCase() === 'k') ||
      (!this.isMac && event.ctrlKey && event.key.toLowerCase() === 'k')
    ) {
      event.preventDefault();
      this.showModal();
    }
  }

  // Setting
  goToSettings(): void {
    this.closeAccountSheet();
    void this.route.navigateByUrl(SETTINGS_PAGE_PATHS.business);
  }

  get displayName(): string {
    if (this.userName?.firstName && this.userName?.lastName) {
      return `${this.userName.firstName} ${this.userName.lastName}`;
    }
    return 'Super Admin';
  }

  openAccountSheet(): void {
    this.accountSheetOpen = true;
    this.onOutletDropdownVisible(true);
  }

  closeAccountSheet(): void {
    this.accountSheetOpen = false;
  }

  onAccountOutletPick(item: any): void {
    const previousId =
      typeof this.selectOutlet === 'object' ? this.selectOutlet?._id : this.selectOutlet;
    this.applyOutlet(item);
    const nextId = typeof item === 'object' ? item?._id : item;
    if (previousId && previousId !== nextId) {
      this.closeAccountSheet();
    }
  }

  // Get outlet
  getAllOutlets() {
    this.outletPageIndex = 1;
    this.allOutlet = [];
    this.rawOutlets = [];
    this.fetchOutlets(true);
  }

  private applyOutletScopeFilter(reselect = false): void {
    this.allOutlet = filterOutletsByUserScope(this.rawOutlets, this.userOutletScope);
    this.commonServices.registerOutlets(this.allOutlet);

    if (!reselect) return;

    const picked = pickScopedOutlet(this.allOutlet, this.selectOutlet);
    if (picked) {
      this.selectOutlet = picked;
      this.applyOutlet(picked);
    } else if (!this.allOutlet.length) {
      this.selectOutlet = {};
      this.commonServices.updateSelectedOutlet({});
    }
  }

  private fetchOutlets(isInitialLoad = false) {
    const tokenData: string =
      localStorage.getItem('TtZUw0Q87E647ac8j0gQHiCmBhczph') || '';
    if (!tokenData) return;

    const requestedPage = this.outletPageIndex;
    if (requestedPage === 1) {
      this.outletsLoading = true;
    }

    const header = {
      headers: {
        Authorization: 'Bearer ' + tokenData,
      },
    };

    this.api
      .commonPostMethod(
        `outlet?page=${requestedPage}&limit=${this.outletLimit}`,
        {},
        header,
      )
      .subscribe(
        (response: any) => {
          const data = response?.data?.outlet || [];

          if (requestedPage === 1) {
            this.rawOutlets = data;
          } else {
            this.rawOutlets = [...this.rawOutlets, ...data];
          }

          this.applyOutletScopeFilter(isInitialLoad);

          this.outletTotalCount =
            response?.data?.pagination?.totalCount ?? this.allOutlet.length;

          this.outletsLoading = false;
          this.outletLoadingMore = false;
        },
        (error: any) => {
          this.outletsLoading = false;
          this.outletLoadingMore = false;
          if (requestedPage > 1) {
            this.outletPageIndex -= 1;
          }
          if (
            error?.error?.message == 'jwt expired' ||
            error?.error?.message == 'jwt must be provided' ||
            error?.error?.message == 'jwt malformed' ||
            error?.error?.message == 'invalid signature' ||
            error?.error?.message == 'Given token user does not exist.' ||
            error?.error?.message == 'invalid token'
          ) {
            this.commonServices.invalidToken();
          }
        },
      );
  }

  onOutletListScroll(event: Event): void {
    const el = event.target as HTMLElement;
    if (!el || this.outletsLoading || this.outletLoadingMore || !this.hasMoreOutlets) {
      return;
    }
    const threshold = 24;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - threshold) {
      this.loadMoreOutlets();
    }
  }

  loadMoreOutlets(): void {
    if (!this.hasMoreOutlets || this.outletLoadingMore || this.outletsLoading) {
      return;
    }
    this.outletLoadingMore = true;
    this.outletPageIndex += 1;
    this.fetchOutlets();
  }

  get hasMoreOutlets(): boolean {
    if (this.outletTotalCount <= 0) {
      return false;
    }
    return this.allOutlet.length < this.outletTotalCount;
  }

  onOutletDropdownVisible(visible: boolean): void {
    if (visible) {
      this.outletPageIndex = 1;
      this.allOutlet = [];
      this.rawOutlets = [];
      this.outletLoadingMore = false;
      this.fetchOutlets();
    }
  }
  applyOutlet(item: any) {
    const previousOutletId = this.selectOutlet
      ? typeof this.selectOutlet === 'object'
        ? this.selectOutlet._id
        : this.selectOutlet
      : '';

    const resolvedOutlet =
      item && typeof item === 'object'
        ? item
        : this.allOutlet.find((o) => o?._id === item) ?? { _id: item };

    this.selectOutlet = resolvedOutlet;
    this.commonServices.updateSelectedOutlet(resolvedOutlet);
    this.selectOutlet = this.commonServices.selectedOutlet.value;

    const newOutletId =
      (this.selectOutlet as any)?._id || resolvedOutlet?._id || '';
    if (previousOutletId && previousOutletId !== newOutletId) {
      // Wipe all — unscoped entries (empty outletId) would survive invalidateOutlet.
      this.apiCache.invalidateAll();
    }
  }

  get currentOutlet(): any | null {
    const selectedId = this.selectOutlet?._id;
    if (!selectedId) {
      return null;
    }
    return (
      this.allOutlet.find((item) => item._id === selectedId) ?? this.selectOutlet
    );
  }

  get otherOutlets(): any[] {
    const selectedId = this.selectOutlet?._id;
    if (!selectedId) {
      return this.allOutlet;
    }
    return this.allOutlet.filter((item) => item._id !== selectedId);
  }

  selectedOutletLabel: string = '';
  selectOutletFn(item: any) {
    this.selectOutlet = item;
    this.selectedOutletLabel = item?.outletName || '';
    this.applyOutlet(item);
  }

  getLoginData() {
    if (!isPlatformBrowser(this.platformId)) return;
    this.userAccess.fetchUserProfile().subscribe((profile) => {
      if (profile) this.userName = profile;
    });
  }

  async logOut(): Promise<void> {
    this.closeAccountSheet();
    const ok = await this.dialog.confirm({
      title: 'Log out?',
      message: 'You will need to sign in again to continue.',
      confirmLabel: 'Log out',
    });
    if (!ok) return;
    this.commonServices.clearLocalStorage();
    this.mediaCache.clearCache();
    this.route.navigate(['/login']);
  }
  ngOnDestroy(): void {
    this.dataShareSubscribe?.unsubscribe();
    this.commonServiceHeader?.unsubscribe();
    this.outletSub?.unsubscribe();
    this.billingSub?.unsubscribe();

    if (this.scrollListener && this.isBrowser) {
      this.document.removeEventListener('scroll', this.scrollListener, true);
    }

    if (this.scrollActiveTimeout) {
      clearTimeout(this.scrollActiveTimeout);
    }

    if (this.membersSearchTimeout) {
      clearTimeout(this.membersSearchTimeout);
    }
  }

  private loadPlanSummary(): void {
    if (!this.isBrowser) return;
    this.billingSub = this.billingApi.getCurrent().subscribe({
      next: (response: any) => {
        const sub = response?.data?.subscription;
        if (!sub) {
          this.planLoaded = false;
          this.cdr.markForCheck();
          return;
        }
        const planName = String(sub.planCode || 'starter');
        const label = planName.charAt(0).toUpperCase() + planName.slice(1);
        this.planIsTrial = sub.status === 'trialing';
        this.planLabel = this.planIsTrial ? `${label} · Trial` : `${label} plan`;
        this.planCta = this.planIsTrial || planName === 'starter' ? 'Upgrade' : 'Manage';

        if (this.planIsTrial && sub.trialEndsAt) {
          const totalMs = 14 * 86400000;
          const leftMs = new Date(sub.trialEndsAt).getTime() - Date.now();
          this.planDaysLeft = Math.max(Math.ceil(leftMs / 86400000), 0);
          this.planProgress = Math.min(Math.max(((totalMs - leftMs) / totalMs) * 100, 0), 100);
        } else {
          this.planDaysLeft = null;
          this.planProgress = 100;
        }
        this.planLoaded = true;
        this.cdr.markForCheck();
      },
      error: () => {
        this.planLoaded = false;
        this.cdr.markForCheck();
      },
    });
  }
}
